import React, { useState, useRef, useEffect } from 'react';
import { Play, FileSpreadsheet, Loader2, CheckCircle, XCircle, X, Clock, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { notifyBatchComplete } from '@/lib/notifications';

// Format seconds to human readable string
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};

export function BatchProcessing({ onBatchComplete, agentVersion = 'v3' }) {
  const [csvFile, setCsvFile] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedBatchId, setCompletedBatchId] = useState(null);
  const csvInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Bitte nur CSV-Dateien hochladen');
      return;
    }
    setCsvFile(file);
    toast.success(`CSV "${file.name}" ausgewählt`);
  };

  const startBatch = async () => {
    if (!csvFile) {
      toast.error('Bitte zuerst eine CSV-Datei auswählen');
      return;
    }

    setIsProcessing(true);
    setBatchStatus(null);

    const formData = new FormData();
    formData.append('file', csvFile);
    if (prompt.trim()) {
      formData.append('user_prompt', prompt.trim());
    }
    formData.append('agent_version', agentVersion);

    try {
      const response = await api.post('/batch/start', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { batch_id, total, message } = response.data;
      setBatchId(batch_id);
      setBatchStatus({
        status: 'processing',
        total,
        completed: 0,
        failed: 0,
      });
      
      toast.success(message);
      pollIntervalRef.current = setInterval(() => pollStatus(batch_id), 3000);
      
    } catch (error) {
      console.error('Error starting batch:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Starten des Batches';
      toast.error(errorMsg);
      setIsProcessing(false);
    }
  };

  const pollStatus = async (id) => {
    try {
      const response = await api.get(`/batch/${id}/status`);
      const data = response.data;
      
      const prevCompleted = batchStatus?.completed || 0;
      setBatchStatus(data);
      
      if (data.completed > prevCompleted && onBatchComplete) {
        onBatchComplete();
      }

      if (data.status === 'completed' || data.status === 'completed_with_errors' || data.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsProcessing(false);
        setCompletedBatchId(id);
        
        const chatsCreated = data.chats_created || data.completed;
        notifyBatchComplete(data.completed, data.failed, data.total);
        
        if (data.status === 'failed') {
          toast.error(`Batch fehlgeschlagen. ${chatsCreated} Chats erstellt.`);
        } else if (data.status === 'completed_with_errors') {
          toast.warning(`${chatsCreated} Chats erstellt, ${data.failed} fehlgeschlagen. Siehe Sidebar.`);
        } else {
          toast.success(`${chatsCreated} neue Chats erstellt! Siehe Sidebar.`);
        }
        
        if (onBatchComplete) {
          onBatchComplete();
        }
      }
    } catch (error) {
      console.error('Error polling batch status:', error);
    }
  };

  const resetBatch = () => {
    setCsvFile(null);
    setPrompt('');
    setBatchId(null);
    setBatchStatus(null);
    setIsProcessing(false);
    setCompletedBatchId(null);
    if (csvInputRef.current) csvInputRef.current.value = '';
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const downloadZip = async () => {
    if (!completedBatchId) return;
    
    try {
      const response = await api.get(`/batch/${completedBatchId}/download-zip`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'batch_results.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim();
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download gestartet');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Download');
    }
  };

  const progress = batchStatus 
    ? Math.round(((batchStatus.completed + batchStatus.failed) / batchStatus.total) * 100)
    : 0;

  return (
    <div className="border rounded-lg bg-card p-4 space-y-4" data-testid="batch-processing">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          Batch-Verarbeitung
        </h3>
        {(batchStatus || csvFile || prompt) && (
          <Button variant="ghost" size="sm" onClick={resetBatch} className="h-8">
            <X className="h-4 w-4 mr-1" /> Zurücksetzen
          </Button>
        )}
      </div>

      {/* File Selection & Prompt */}
      {!batchStatus && (
        <div className="space-y-3">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="batch-csv-input"
          />
          
          {/* CSV File - Required */}
          <Button
            variant="outline"
            onClick={() => csvInputRef.current?.click()}
            disabled={isProcessing}
            className="w-full justify-start"
            data-testid="select-csv-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2 shrink-0" />
            <span className="truncate">
              {csvFile ? csvFile.name : 'CSV-Datei auswählen *'}
            </span>
            {csvFile && <CheckCircle className="h-4 w-4 ml-auto text-green-500 shrink-0" />}
          </Button>

          {/* User Prompt Input */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Anweisung für alle CRs (optional):
            </label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="z.B. 'Bewerte mit Fokus auf Sicherheitsaspekte' oder 'Prüfe besonders die technische Machbarkeit'"
              disabled={isProcessing}
              className="min-h-[60px] text-sm"
              data-testid="batch-prompt-input"
            />
          </div>

          {/* Start Button */}
          <Button
            onClick={startBatch}
            disabled={!csvFile || isProcessing}
            className="w-full"
            data-testid="start-batch-btn"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Batch starten
              </>
            )}
          </Button>
          
          <p className="text-xs text-muted-foreground">
            * CSV mit CR-Daten hochladen. Neue Chats erscheinen in der Sidebar.
          </p>
        </div>
      )}

      {/* Progress Display */}
      {batchStatus && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {batchStatus.status === 'processing' ? 'Verarbeitung läuft...' : 'Abgeschlossen'}
            </span>
            <span className="font-medium">
              {batchStatus.completed + batchStatus.failed}/{batchStatus.total}
            </span>
          </div>
          
          <Progress value={progress} className="h-2" />
          
          <div className="flex gap-4 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              {batchStatus.completed} erfolgreich
            </span>
            {batchStatus.failed > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <XCircle className="h-4 w-4" />
                {batchStatus.failed} fehlgeschlagen
              </span>
            )}
          </div>

          {/* Summary */}
          {batchStatus.status === 'completed' && batchStatus.summary && (
            <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
              <h4 className="font-medium mb-2">Zusammenfassung</h4>
              <div className="grid grid-cols-4 gap-2 text-center">
                {Object.entries(batchStatus.summary).map(([rec, count]) => (
                  <div key={rec} className="p-2 bg-background rounded">
                    <div className="text-lg font-bold">{count}</div>
                    <div className="text-xs text-muted-foreground">{rec}</div>
                  </div>
                ))}
              </div>
              
              {/* Duration Info */}
              {(batchStatus.duration_seconds || batchStatus.avg_duration_seconds) && (
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Gesamt: <strong className="text-foreground">{formatDuration(batchStatus.duration_seconds)}</strong>
                  </span>
                  <span>|</span>
                  <span>
                    Ø pro CR: <strong className="text-foreground">{formatDuration(Math.round(batchStatus.avg_duration_seconds))}</strong>
                  </span>
                </div>
              )}
              
              {/* Download ZIP Button */}
              {completedBatchId && (
                <Button
                  onClick={downloadZip}
                  className="w-full mt-4"
                  variant="default"
                  data-testid="download-zip-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Alle Ergebnisse als ZIP herunterladen
                </Button>
              )}
              
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Die neuen Chats findest du in der Sidebar links.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
